import { useEffect, useRef, useState } from 'react'
import { tournamentApi } from '../services/api'

/**
 * Shared "Course photo" editor — the same modal the admin Course Integrations
 * page uses, reused by the reseller Connected Courses page so there's ONE way to
 * set a course photo (paste URL / upload / Find on Google / pick a Golf Trail
 * mirror + preview). Endpoints are passed in so admin and reseller point at
 * their own scoped routes.
 *
 * Props:
 *   courseId, courseName, city, imageUrl   – the course + its current photo
 *   imagesUrl   – GET Golf Trail mirrored images (optional; hidden if omitted)
 *   uploadUrl, uploadField  – POST multipart upload -> { url }
 *   saveUrl     – PATCH { imageUrl } -> { imageUrl }
 *   allowClear  – show the "Clear photo" action (admin only; resellers fill-missing)
 *   t           – optional i18n (key, fallback) => string; defaults to English
 *   onClose, onSaved({ imageUrl })
 */
export default function CoursePhotoModal({
  courseId, courseName, city, imageUrl,
  imagesUrl, uploadUrl, uploadField = 'file', saveUrl,
  allowClear = false,
  t = (_k, f) => f,
  onClose, onSaved,
}) {
  const [url, setUrl] = useState(imageUrl || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [synced, setSynced] = useState([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)
  const trimmed = (url || '').trim()
  const looksValid = trimmed === '' || /^https?:\/\//i.test(trimmed)

  useEffect(() => {
    if (!imagesUrl) return
    tournamentApi.get(imagesUrl)
      .then(({ data }) => setSynced(data.images || []))
      .catch(() => setSynced([]))
  }, [imagesUrl])

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true); setError(null)
    try {
      const fd = new FormData()
      fd.append(uploadField, file)
      const { data } = await tournamentApi.post(uploadUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (data?.url) setUrl(data.url)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || t('sp_cc_photo_failed', 'Upload failed'))
    } finally { setUploading(false) }
  }

  async function save() {
    if (trimmed && !looksValid) {
      setError(t('sp_cc_url_invalid', 'Enter a full http(s):// image URL, or clear the field to remove the photo.'))
      return
    }
    setSaving(true); setError(null)
    try {
      const { data } = await tournamentApi.patch(saveUrl, { imageUrl: trimmed || null })
      onSaved({ imageUrl: data?.imageUrl ?? (trimmed || null) })
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
      setSaving(false)
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={body} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>{t('sp_cc_title', 'Course photo')}</h2>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{courseName}</div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        <div style={{ marginBottom: 12 }}>
          <label style={upLabel}>{t('sp_cc_scenic_label', 'Scenic photo URL')}</label>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
            {t('sp_cc_help', "Paste a full http(s) link to a landscape photo, or upload one from your computer. Shown to customers on the trip itinerary's golf rows. Leave blank to fall back to the course logo.")}
          </div>
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder={t('sp_cc_url_ph', 'https://…/course-hero.jpg  (or use Upload / Find on Google below)')}
            style={{ ...input, width: '100%' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1, fontFamily: 'inherit' }}>
              {uploading ? t('sp_cc_uploading', 'Uploading…') : `⬆ ${t('sp_cc_upload', 'Upload from computer')}`}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
            <button type="button"
              onClick={() => {
                const q = [courseName, city].filter(Boolean).join(' ').trim() + ' golf course'
                window.open(`https://www.google.com/search?q=${encodeURIComponent(q)}`, '_blank', 'noopener,noreferrer')
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, background: '#fff', color: '#1a73e8', border: '1px solid #dadce0', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
              🔍 {t('sp_cc_find_google', 'Find on Google')} ↗
            </button>
          </div>
        </div>

        {trimmed && looksValid && (
          <div style={{ marginBottom: 12 }}>
            <div style={sectionLabel}>{t('sp_cc_preview', 'Preview')}</div>
            <img src={trimmed} alt="" style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc' }}
              onError={(e) => { e.currentTarget.style.display = 'none' }} />
          </div>
        )}

        {synced.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={sectionLabel}>{t('sp_cc_from_golftrail', 'From Golf Trail ({n})').replace('{n}', synced.length)}</div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 8 }}>
              {t('sp_cc_golftrail_help', "Mirrored automatically. Click one to use it as this course's photo.")}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {synced.map(im => {
                const selected = trimmed && (trimmed === im.LocalUrl || trimmed.endsWith(im.LocalUrl))
                return (
                  <button key={im.ID_NO} type="button" onClick={() => setUrl(im.LocalUrl)}
                    title={`${im.Kind}${im.Position != null ? ` #${im.Position}` : ''}${im.Attribution ? ` · ${im.Attribution}` : ''}`}
                    style={{ position: 'relative', padding: 0, border: selected ? '2px solid #00d19f' : '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#f8fafc', lineHeight: 0 }}>
                    <img src={im.LocalUrl} alt={im.Kind} style={{ width: 96, height: 66, objectFit: 'cover', borderRadius: 6, display: 'block' }} onError={(e) => { e.currentTarget.style.opacity = 0.3 }} />
                    <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 9, fontWeight: 700, color: '#fff', background: im.Kind === 'cover' ? '#0f172a' : im.Kind === 'google_place' ? '#7c3aed' : '#334155', padding: '1px 5px', borderRadius: 4 }}>{im.Kind === 'google_place' ? 'google' : im.Kind}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 16 }}>
          <div>
            {allowClear && imageUrl && (
              <button onClick={() => setUrl('')} style={{ ...smallBtn, color: '#b91c1c', borderColor: '#fecaca' }}>{t('sp_cc_clear', 'Clear photo')}</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={smallBtn}>{t('sp_cc_cancel', 'Cancel')}</button>
            <button onClick={save} disabled={saving}
              style={{ padding: '8px 16px', background: '#00d19f', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
              {saving ? t('sp_cc_saving', 'Saving…') : t('sp_cc_save', 'Save photo')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }
const body = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(15,23,42,0.3)' }
const closeBtn = { border: 'none', background: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: 0 }
const errorBox = { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 }
const input = { padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', boxSizing: 'border-box' }
const upLabel = { display: 'block', fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }
const sectionLabel = { fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }
const smallBtn = { padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' }
